import { TFunction } from "i18next";
import { LanguageCode } from "../models/enums/LanguageCode";

/**
 * The value to display for a translatable field, in the language currently being viewed.
 *
 * The entity's own column is NOT a French value. It carries whatever the talent typed until the
 * worker produces the primary-language translation and the API mirrors it back into it, so its
 * language follows the talent, not the platform. It is therefore a valid fallback ONLY for the
 * language the talent writes in.
 *
 * Falling back to it for the other language is what displayed a talent's English text under the
 * French flag: indistinguishable, for the reader, from a French version that exists and happens
 * to be in English.
 */
export const resolveTranslatableFieldValue = (
	cellValue: string | undefined,
	columnValue: string | null | undefined,
	viewingLanguage: LanguageCode | undefined,
	sourceLanguage: LanguageCode
): string => {
	if (cellValue) {
		return cellValue;
	}

	// An undefined viewing language means the screen offers no language switch at all: there is
	// only one version to show, and it is the column.
	const viewingTheSourceLanguage = !viewingLanguage || viewingLanguage === sourceLanguage;

	return viewingTheSourceLanguage ? columnValue || "" : "";
};

/**
 * What to display in place of an empty translatable field.
 *
 * A talent who left a field blank and one who wrote it in their own language are two different
 * situations. "Not provided" states the first for both, and sends the reader hunting for content
 * that is one flag away.
 */
export const translatableFieldEmptyLabel = (
	t: TFunction,
	viewingLanguage: LanguageCode | undefined,
	sourceLanguage: LanguageCode
): string => {
	if (!viewingLanguage || viewingLanguage === sourceLanguage) {
		return t("freelancerDetails.notProvided");
	}

	const nameOf = (language: LanguageCode) =>
		t(`translations.languageName${language === LanguageCode.En ? "En" : "Fr"}`);

	return t("freelancerDetails.noVersionInThisLanguage", {
		language: nameOf(viewingLanguage),
		source: nameOf(sourceLanguage)
	});
};
